import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { buildPath, nameof } from '@e-socna/utils';

import { PATH_SECTION_TEMPLATE_DEFAULT } from '../../../app/constants/paths.constant';
import type { ReferencesContext_GenericStatus } from '../../../contexts/ReferencesContext';

import { STATUS_ID } from '../constants/data-grid.constant';
import type { SectionTemplatesPage_List_SectionTemplate } from '../hooks/useFindAllSectionTemplatesQuery.hook';

interface Props {
  readonly sectionTemplate: SectionTemplatesPage_List_SectionTemplate;
}

const Action = ({ sectionTemplate }: Readonly<Props>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(PATH_SECTION_TEMPLATE_DEFAULT, {
      id: sectionTemplate._id,
    });
    void navigate(path);
  }, [navigate, sectionTemplate._id]);

  const actionLabel = 'Consulter le Modèle de Section';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getColumns = (
  genericStatuses: readonly ReferencesContext_GenericStatus[],
): readonly GridColDef<SectionTemplatesPage_List_SectionTemplate>[] => [
  {
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    editable: false,
    sortable: false,
    type: 'actions',
    getActions: (
      params: GridRowParams<SectionTemplatesPage_List_SectionTemplate>,
    ) => [<Action key="consult" sectionTemplate={params.row} />],
  },
  {
    field: nameof<SectionTemplatesPage_List_SectionTemplate>('code'),
    headerName: 'Code',
    width: 350,
    description: 'Code',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: nameof<SectionTemplatesPage_List_SectionTemplate>('label'),
    headerName: 'Libellé',
    width: 400,
    description: 'Libellé',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: STATUS_ID,
    headerName: 'Statut',
    width: 200,
    description: 'Statut',
    editable: false,
    sortable: true,
    hideable: true,
    type: 'singleSelect',
    valueGetter: (_: unknown, row: SectionTemplatesPage_List_SectionTemplate) =>
      genericStatuses.find(
        (genericStatus) => genericStatus._id === row.statusId,
      )?.code ?? 'Inconnu',
    valueOptions: genericStatuses.map((genericStatus) => ({
      value: genericStatus.code,
      label: genericStatus.label,
    })),
  },
];
